// Phase 5f-e round-1 audit closure — caregiver portal section-visibility gates.
//
// Split from a single `showEditForm` boolean (Phase 5f-a) into per-section
// gates because availability has a fundamentally different review semantics:
//
//   - Profile / credentials / compliance: frozen during UNDER_REVIEW so the
//     operator's review cannot be invalidated mid-flight.
//   - Availability: NOT part of operator review. The matching engine reads
//     it independently. Phase 5g moved availability collection POST-approval:
//     the section unlocks at ONBOARDING and stays through ACTIVE, and is
//     hidden for APPLYING / UNDER_REVIEW. It is NOT a gate on any transition
//     (the admin approve guard AND `checkOnboardingComplete` both omit it),
//     and — as of the 2026-08-11 fix — no longer a line on the APPLYING
//     submit checklist either, so a fresh applicant is never asked to add
//     availability with no UI to do it in.
//
// Backend `CaregiverAvailabilityService.create()` has no status guard — the
// API tolerates writes in any status; the portal gate is the deliberate
// layer that scopes availability editing to ONBOARDING+ per 5g.
//
// Phase 5f-e round-2 audit closure: type is the canonical `CaregiverStatus`
// from `@truecare/schemas` (not a local duplicate). If the schema enum gains
// a new value, the helper here would fail TypeScript — exhaustiveness check
// preserved.

import type { CaregiverStatus } from '@truecare/schemas';

export interface CaregiverSectionGates {
  showProfileEdit: boolean;
  showCredentialsEdit: boolean;
  showComplianceEdit: boolean;
  showAvailabilityEdit: boolean;
  showSubmitPanel: boolean;
  /** Phase 5g — the ONBOARDING checklist (banking + SIN + contract) is the
   *  caregiver's centrepiece while they complete onboarding. */
  showOnboarding: boolean;
  /** Phase 5g (Q4 / change-list 1.4) — banking + SIN + HST are collected in
   *  the ONBOARDING stage and editable while ACTIVE; hidden before approval
   *  so an APPLYING / UNDER_REVIEW caregiver never sees the SIN field. */
  showPaymentTax: boolean;
}

export function caregiverSectionGates(
  status: CaregiverStatus,
): CaregiverSectionGates {
  const isApplying = status === 'APPLYING';
  const isActive = status === 'ACTIVE';
  const isOnboarding = status === 'ONBOARDING';

  // Credentials + compliance stay frozen post-approval (re-vetting concern —
  // a swapped credential would need operator re-review). Profile, by contrast,
  // is editable during ONBOARDING (Phase 5g audit C1): the review-freeze
  // rationale is spent once approved, and a caregiver who moves mid-onboarding
  // must be able to correct their address.
  const docsEditable = isApplying || isActive;

  return {
    showProfileEdit: isApplying || isOnboarding || isActive,
    showCredentialsEdit: docsEditable,
    showComplianceEdit: docsEditable,
    // Phase 5g (change-list 1.3) — availability is collected POST-approval,
    // so it unlocks at ONBOARDING and stays through ACTIVE. It is NOT shown to
    // APPLYING / UNDER_REVIEW caregivers (the 5f-e deadlock that once forced it
    // pre-approval is gone: 5g removed availability from every activation gate).
    showAvailabilityEdit: isOnboarding || isActive,
    showSubmitPanel: isApplying,
    showOnboarding: isOnboarding,
    showPaymentTax: isOnboarding || isActive,
  };
}
